import React, { useState, useEffect } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { Box, Typography } from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

export default function RecursiveTreeView(props) {
  const { isDark = false, data: dataProp } = props;
  const [expandedItems, setExpandedItems] = useState(() => Array.isArray(dataProp?.defaultExpanded) ? dataProp.defaultExpanded : []);

  const greenColor = "#A0C46D";
  const hoverBg = isDark ? "rgba(160, 196, 109, 0.1)" : "#f1f5f9";
  const selectedBg = isDark ? "rgba(160, 196, 109, 0.2)" : "#e0f2fe";
  const selectedHoverBg = isDark ? "rgba(160, 196, 109, 0.3)" : "#bae6fd";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.3)" : "#e2e8f0";
  const textColor = isDark ? "#e2e8f0" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";

  const classes = {
    root: {
      flexGrow: 1,
      width: "100%",
      textAlign: "left",
      // Child rows: compact
      "& .MuiTreeItem-content": {
        padding: "3px 6px",
        marginBottom: "2px",
        borderRadius: "4px",
        justifyContent: "flex-start",
        textAlign: "left",
        minHeight: 28,
        transition: "background-color 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          backgroundColor: hoverBg,
        },
        "&.Mui-selected": {
          backgroundColor: selectedBg,
          "&:hover": {
            backgroundColor: selectedHoverBg,
          },
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `inset 0 0 0 2px ${greenColor}`,
        },
      },
      // Parent/root row: distinct header block (visual hierarchy), compact
      "& > .MuiTreeItem-root > .MuiTreeItem-content": {
        backgroundColor: isDark ? "rgba(160, 196, 109, 0.2)" : "rgba(160, 196, 109, 0.15)",
        border: `2px solid ${greenColor}`,
        borderRadius: "6px",
        padding: "5px 8px",
        minHeight: 34,
        marginBottom: "4px",
        fontWeight: 600,
        boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.2)" : "0 1px 4px rgba(0,0,0,0.08)",
        transition: "background-color 0.2s ease, box-shadow 0.2s ease",
        "& .MuiTreeItem-label": {
          fontSize: "0.75rem",
          fontWeight: 600,
        },
        "&:hover": {
          backgroundColor: isDark ? "rgba(160, 196, 109, 0.28)" : "rgba(160, 196, 109, 0.2)",
          boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.25)" : "0 2px 6px rgba(0,0,0,0.1)",
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `inset 0 0 0 2px ${greenColor}, 0 2px 8px rgba(0,0,0,0.2)`,
        },
      },
      "& .MuiTreeItem-group": {
        marginLeft: "24px",
        marginTop: "2px",
        paddingLeft: "12px",
        borderLeft: `2px solid ${borderColor}`,
      },
      "& .MuiTreeItem-label": {
        color: textColor,
        textAlign: "left",
      },
      "& .MuiTreeItem-iconContainer": {
        minWidth: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      "@media (prefers-reduced-motion: reduce)": {
        "& .MuiTreeItem-content": { transition: "none" },
        "& > .MuiTreeItem-root > .MuiTreeItem-content": { transition: "none" },
        "& .MuiCollapse-root": { transition: "none !important" },
      },
    },
    label: {
      fontFamily: "Lato, sans-serif",
      fontWeight: 500,
      fontSize: "0.7rem",
      lineHeight: 1.25,
      color: textColor,
    },
  };

  const data = React.useMemo(() => dataProp, [dataProp]);

  useEffect(() => {
    if (Array.isArray(data?.defaultExpanded) && data.defaultExpanded.length > 0) {
      setExpandedItems(data.defaultExpanded);
    }
  }, [data?.defaultExpanded]);

  const renderTree = (nodes) => {
    if (!nodes || nodes.id == null) return null;
    return (
      <TreeItem
        classes={{ label: classes.label }}
        itemId={nodes.id}
        key={nodes.id}
        label={nodes.label}
      >
        {Array.isArray(nodes.children) && nodes.children.length > 0
          ? nodes.children.map((node) => renderTree(node))
          : null}
      </TreeItem>
    );
  };

  if (!data) {
    return (
      <Box
        role="status"
        aria-label="No hierarchy data"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 120,
          gap: 1,
          py: 2,
        }}
      >
        <AccountTreeIcon sx={{ fontSize: "2rem", color: isDark ? "rgba(148, 163, 184, 0.3)" : "rgba(100, 116, 139, 0.3)" }} />
        <Typography variant="body2" sx={{ color: textSecondary, fontWeight: 600 }}>
          No hierarchy data available
        </Typography>
      </Box>
    );
  }

  return (
    <SimpleTreeView
      sx={classes.root}
      aria-label="Asset hierarchy tree"
      defaultCollapseIcon={
        <ExpandMoreIcon sx={{ color: isDark ? greenColor : "action.active", fontSize: "1.1rem" }} />
      }
      expandedItems={Array.isArray(expandedItems) ? expandedItems : []}
      onExpandedItemsChange={(_event, itemIds) => setExpandedItems(Array.isArray(itemIds) ? itemIds : [])}
      defaultSelected={data?.defaultSelected || "root"}
      defaultExpandIcon={
        <ChevronRightIcon sx={{ color: isDark ? "rgba(148, 163, 184, 0.6)" : "action.active", fontSize: "1.1rem" }} />
      }
      onNodeSelect={() => { }}
    >
      {renderTree(data)}
    </SimpleTreeView>
  );
}
